/* Version: 3.5.149 */
'use client';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import { useCallback } from 'react';
import PropTypes from 'prop-types';
import Checkbox from '../Checkbox/index.js';
import { ALIGNMENT, DIRECTION } from '../constants.js';
import Errors from '../Errors/Errors.js';
import Legend from '../Legend/Legend.js';
import { safeRest, hasErrors, noop } from '../../utils/functions.js';
import handle from '../../utils/handle.js';
import safeCustomStyle from '../../utils/safeCustomStyle.js';
import { Root, Fieldset, FlexBox, ItemGroup } from './styledComponents.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import useDerivedState from '../../hooks/useDerivedState.js';
import useId from '../../hooks/useId.js';
import componentWithName from '../../prop-types/componentWithName.js';

var _excluded = ["id", "legend", "values", "selected", "alignment", "direction", "mobileDirection", "ariaLabel", "ariaRequired", "disabled", "errors", "errorCallout", "onChange", "onBlur", "tooltip", "customStyle"];
var CheckboxGroup = function CheckboxGroup(_ref) {
  var controlledId = _ref.id,
    _ref$legend = _ref.legend,
    legend = _ref$legend === undefined ? '' : _ref$legend,
    _ref$values = _ref.values,
    values = _ref$values === undefined ? [] : _ref$values,
    _ref$selected = _ref.selected,
    selected = _ref$selected === undefined ? {} : _ref$selected,
    _ref$alignment = _ref.alignment,
    alignment = _ref$alignment === undefined ? ALIGNMENT.START : _ref$alignment,
    _ref$direction = _ref.direction,
    direction = _ref$direction === undefined ? DIRECTION.COLUMN : _ref$direction,
    _ref$mobileDirection = _ref.mobileDirection,
    mobileDirection = _ref$mobileDirection === undefined ? DIRECTION.COLUMN : _ref$mobileDirection,
    _ref$ariaLabel = _ref.ariaLabel,
    ariaLabel = _ref$ariaLabel === undefined ? '' : _ref$ariaLabel,
    _ref$ariaRequired = _ref.ariaRequired,
    ariaRequired = _ref$ariaRequired === undefined ? false : _ref$ariaRequired,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === undefined ? false : _ref$disabled,
    _ref$errors = _ref.errors,
    errors = _ref$errors === undefined ? [] : _ref$errors,
    _ref$errorCallout = _ref.errorCallout,
    errorCallout = _ref$errorCallout === undefined ? false : _ref$errorCallout,
    _ref$onChange = _ref.onChange,
    onChange = _ref$onChange === undefined ? noop : _ref$onChange,
    _ref$onBlur = _ref.onBlur,
    onBlur = _ref$onBlur === undefined ? noop : _ref$onBlur,
    tooltip = _ref.tooltip,
    _ref$customStyle = _ref.customStyle,
    customStyle = _ref$customStyle === undefined ? {
      rootStyle: {},
      legendStyle: {},
      checkboxStyle: {},
      labelStyle: {}
    } : _ref$customStyle,
    rest = _objectWithoutProperties(_ref, _excluded);
  var _useDerivedState = useDerivedState(useId('checkbox-id-'), controlledId),
    _useDerivedState2 = _slicedToArray(_useDerivedState, 1),
    id = _useDerivedState2[0];
  var safeProps = safeRest(rest);
  var hasError = hasErrors(errors);
  var errorsId = hasError ? "".concat(id, "-errors") : undefined;
  var _safeCustomStyle = safeCustomStyle(customStyle),
    rootStyle = _safeCustomStyle.rootStyle;
  var memoizedHandleToggle = useCallback(function (value, event) {
    handle(onChange)(event, _objectSpread(_objectSpread({}, selected), {}, _defineProperty({}, value, !selected[value])));
  }, [onChange, selected]);
  var memoizedHandleBlur = useCallback(function (_value, event) {
    handle(onBlur)(event, selected);
  }, [onBlur, selected]);
  return /*#__PURE__*/jsx(Root, {
    rootStyle: rootStyle,
    children: /*#__PURE__*/jsxs(Fieldset, {
      id: id,
      children: [/*#__PURE__*/jsx(Legend, {
        customStyle: {
          legendStyle: customStyle.legendStyle
        },
        tooltip: tooltip,
        children: legend
      }), /*#__PURE__*/jsx(FlexBox, {
        "data-testid": "checkbox-group-test",
        alignment: alignment,
        direction: direction,
        mobileDirection: mobileDirection,
        children: values.map(function (e) {
          var label = e.label,
            value = e.value,
            valueDisabled = e.disabled;
          var isChecked = !!selected[value];
          var isDisabled = disabled || valueDisabled;
          var checkboxId = "".concat(id, "-").concat(value);
          return /*#__PURE__*/jsx(ItemGroup, {
            direction: direction,
            mobileDirection: mobileDirection,
            "data-testid": "checkbox-wrapper-".concat(value),
            children: /*#__PURE__*/jsx(Checkbox, _objectSpread(_objectSpread({}, safeProps), {}, {
              id: checkboxId,
              checked: isChecked,
              "aria-describedby": errorsId,
              "aria-required": ariaRequired,
              "aria-label": ariaLabel,
              onChange: isDisabled ? noop : function (_checked, event) {
                return memoizedHandleToggle(value, event);
              },
              onBlur: isDisabled ? noop : function (_checked, event) {
                return memoizedHandleBlur(value, event);
              },
              disabled: isDisabled,
              customStyle: {
                checkboxStyle: customStyle.checkboxStyle,
                labelStyle: customStyle.labelStyle
              },
              error: hasError,
              children: label
            }))
          }, value);
        })
      }), /*#__PURE__*/jsx(Errors, {
        id: id,
        errors: errors,
        callouts: errorCallout
      })]
    })
  });
};
process.env.NODE_ENV !== "production" ? CheckboxGroup.propTypes = {
  /** Global [id](https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/id) attribute. */
  id: PropTypes.string,
  /** Label passed to the fieldset [legend](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/legend) element. */
  legend: PropTypes.string,
  /** Array of selectable labels/values. */
  values: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
    disabled: PropTypes.bool
  })),
  /** Object containing boolean key-value pairs of which value(s) are currently selected. */
  selected: PropTypes.object,
  /** Specifies the alignment of checkboxes within the group. */
  alignment: PropTypes.oneOf([ALIGNMENT.CENTER, ALIGNMENT.END, ALIGNMENT.SPACE_AROUND, ALIGNMENT.SPACE_BETWEEN, ALIGNMENT.START]),
  /** Specifies the direction of checkbox group (row or column). */
  direction: PropTypes.oneOf([DIRECTION.ROW, DIRECTION.COLUMN]),
  /** Specifies the direction of checkbox group on small screens (`<=576px`). */
  mobileDirection: PropTypes.oneOf([DIRECTION.ROW, DIRECTION.COLUMN]),
  /** WAI-ARIA [aria-label](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-label) attribute. */
  ariaLabel: PropTypes.string,
  /** WAI-ARIA [aria-required](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-required) attribute. */
  ariaRequired: PropTypes.bool,
  /** HTML [disabled](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/disabled) attribute. */
  disabled: PropTypes.bool,
  /** An array of error messages to be displayed. */
  errors: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    msg: PropTypes.any,
    isError: PropTypes.bool
  }))]),
  /** Sets `aria-atomic=true`, `aria-live=polite` and `role=alert` on error messages (ensures errors are announced by screen readers as soon as they happen). */
  errorCallout: PropTypes.bool,
  /** Checkbox group [change](https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/change_event) event handler. */
  onChange: PropTypes.func,
  /** Checkbox group [blur](https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/blur) event handler. */
  onBlur: PropTypes.func,
  /** Style overrides. */
  customStyle: PropTypes.shape({
    rootStyle: PropTypes.object,
    legendStyle: PropTypes.object,
    checkboxStyle: PropTypes.object,
    labelStyle: PropTypes.object
  }),
  /** [Tooltip](/docs/tooltip) to be displayed beside the label. */
  tooltip: componentWithName('Tooltip')
} : undefined;
process.env.NODE_ENV !== "production" ? CheckboxGroup.displayName = 'CheckboxGroup' : undefined;

export { CheckboxGroup as default };
